import { Link } from 'gatsby'
import { Image } from 'src/components/ui/Image'
import classesnames from 'classnames'
import { useCallback } from 'react'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

type Props = {
  image: {
    tablet: string
    desktop: string
    mobile: string
  }
  title: {
    text: string
    textColor: 'preto' | 'branco'
  }
  button?: {
    text: string
    textColor: 'preto' | 'branco'
    backgroundColor: 'preto' | 'branco'
    linkURL: string
  }
  section: string
  locationOnPage: string
}
export function BannerHero({
  image,
  title,
  button,
  section,
  locationOnPage,
}: Props) {
  const { pageData } = useGlobalContext()
  const token = {
    text: {
      branco: 'text-restructure-primary-inverted',
      preto: 'text-restructure-primary',
    },
    background: {
      branco: 'bg-restructure-background-primary',
      preto: 'bg-restructure-background-primary-inverted',
    },
  }

  const standardObject = getStandardObject(pageData)

  const handleClickBanner = useCallback(() => {
    const featureObject = getFeatureObject({
      section,
      locationOnPage,
      name: title?.text ?? '',
      index: 4,
    })

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...standardObject,
        ...featureObject,
      },
    })

    if (window === null) {
      return
    }

    window.dataLayer = window?.dataLayer ?? []

    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'promotionClick',
      ecommerce: {
        promoClick: {
          promotions: [
            {
              id: title,
              name: title,
              creative: image,
              position: 'slot1',
            },
          ],
        },
      },
    })
  }, [image, locationOnPage, section, standardObject, title])

  return (
    <Link
      className="overflow-hidden relative flex-1 min-h-[328px] restructure-tablet:min-h-[310px] restructure-small-desktop:min-h-[500px] rounded-2xl  bg-restructure-background-primary flex items-end "
      to={button?.linkURL ?? ''}
      onClick={() => handleClickBanner()}
    >
      {/* <span className="absolute top-0 block w-full h-full rounded-2xl bg-linearCard z-10" /> */}
      <Image
        src={image.mobile}
        alt={title.text}
        width={398}
        height={328}
        className="object-cover absolute top-0 left-0 w-full h-full rounded-md restructure-tablet:hidden transition-transform duration-500 ease-in-out hover:scale-110"
      />
      <Image
        src={image.tablet}
        alt={title.text}
        width={391}
        height={310}
        className="object-cover absolute top-0 left-0 w-full h-full rounded-md hidden restructure-tablet:block restructure-small-desktop:hidden transition-transform duration-500 ease-in-out hover:scale-110"
      />
      <Image
        src={image.desktop}
        alt={title.text}
        width={631}
        height={500}
        className="object-cover absolute top-0 left-0 w-full h-full rounded-md hidden restructure-small-desktop:block transition-transform duration-500 ease-in-out hover:scale-110"
      />
      <div className="relative px-6 restructure-small-desktop:px-xl py-6 restructure-small-desktop:py-[40px] z-20">
        {title.text !== '' && (
          <h2
            data-testid="titleBanner"
            className={classesnames(
              'mobile-heading-title3 tablet-mobile-heading-title3 desktop-heading-title3',
              token.text[title.textColor]
            )}
          >
            {title.text}
          </h2>
        )}
        {button && (
          <button
            className={classesnames(
              'h-[48px] mt-lg px-md py-xxs rounded-round desktop-body-semibold-text2 items-center justify-center gap-xs w-fit hidden restructure-small-desktop:flex hover:bg-restructure-background-tertiary focus:bg-restructure-background-disabled',
              token.background[button.backgroundColor],
              token.text[button.textColor]
            )}
          >
            <span>{button.text}</span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.5415 10C3.5415 9.65482 3.82133 9.375 4.1665 9.375H15.8332C16.1783 9.375 16.4582 9.65482 16.4582 10C16.4582 10.3452 16.1783 10.625 15.8332 10.625H4.1665C3.82133 10.625 3.5415 10.3452 3.5415 10Z"
                fill="#15181B"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.55806 3.72456C9.80214 3.48048 10.1979 3.48048 10.4419 3.72456L16.2753 9.5579C16.5194 9.80197 16.5194 10.1977 16.2753 10.4418L10.4419 16.2751C10.1979 16.5192 9.80214 16.5192 9.55806 16.2751C9.31398 16.031 9.31398 15.6353 9.55806 15.3912L14.9495 9.99984L9.55806 4.60845C9.31398 4.36437 9.31398 3.96864 9.55806 3.72456Z"
                fill="#15181B"
              />
            </svg>
          </button>
        )}
      </div>
    </Link>
  )
}
